import React, {useEffect, useContext, useCallback, useMemo} from "react";

import UIPopupContext from "../../popups/UIPopup/UIPopupContext";
import UiIcon from "../../ui/icons/uiIcon";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import IconButton from "@mui/material/IconButton";
import {
  createPrompt,
  getCurrentPrompts,
  removePrompt,
  getCurrentPromptParts,
  createPromptsForNewPartObject
} from "../aiApi";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {getCommonVisualizerType} from "../../../config";
import {CloneIcon} from "../../common/icons";
import LoadingSpinner from "../../ui/loadingSpinner";


const UiPopupAIContentPrompts = (props) => {

  const {type} = props;

  const {uiPopupContext, uiPopupDispatch} = useContext(UIPopupContext);
  const {promptsList, groupObjects} = uiPopupContext;

  const isActiveType = "isActive" + type;
  const isActiveBody = "partBody" + type;
  const promptsPartsLoading = "promptsPartsLoading" + type;
  const savedPromptPartObject = "savedPromptPartObject" + type;

  const renderPromptsList = useMemo(() => {
    if (!promptsList) return [];
    return promptsList.filter(pr => pr.type === type);
  }, [promptsList, type]);

  const activePrompt = useMemo(() => {
    return renderPromptsList.find(pr => pr[isActiveType]) || null;
  }, [renderPromptsList, isActiveType]);

  const activePromptId = activePrompt ? activePrompt.id : null;

  const setActivePrompt = useCallback((prObject) => {
    uiPopupDispatch((state) => ({
      ...state,
      promptsList: (state.promptsList || []).map(p => ({
        ...p,
        [isActiveType]: p.id === prObject.id,
      })),
      [promptsPartsLoading]: true,
    }));
  }, [isActiveType, promptsPartsLoading, uiPopupDispatch]);

  useEffect(() => {
    if (!activePrompt || !groupObjects?.length) return;

    let cancelled = false;

    (async () => {
      try {
        const prPartsResponse = await getCurrentPromptParts(activePrompt);
        if (cancelled) return;
        console.log("prPartsResponse", prPartsResponse);
        uiPopupDispatch((state) => {
          const latestGroups = state.groupObjects || [];

          return {
            ...state,
            [promptsPartsLoading]: false,
            groupObjects: latestGroups.map(groupObject => {
              const fgName = groupObject.featureGroup.dbName || groupObject.featureGroup.partName;
              const foundPartForThisPrompt = prPartsResponse.find(prPartObject => prPartObject.featureGroup === fgName);
              return {
                ...groupObject,
                [isActiveBody]: foundPartForThisPrompt ? foundPartForThisPrompt.partBody : "",
                [savedPromptPartObject]: foundPartForThisPrompt || null,
              };
            }),
          };
        });
      } catch (e) {
        if (!cancelled) uiPopupDispatch({ [promptsPartsLoading]: false });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePromptId]);

  if (!promptsList) {
    return null
  }

  const onDeleteClick = async (prObject) => {
    uiPopupDispatch({
      promptsList: promptsList.filter(groupObject => {
        return groupObject.id !== prObject.id
      }),
    });
    await removePrompt(prObject);
  }

  const onCreateClick = async () => {
    const newObj = {
      "id": 0,
      "tool": getCommonVisualizerType(),
      "type": type,
      "version": 0,
      "promptBody": "New Prompt"
    }
    await createPrompt(newObj);
    const prompts = await getCurrentPrompts();
    const responseWithId = prompts[prompts.length - 1];
    if (responseWithId) {
      newObj.id = responseWithId.id;
      newObj.version = responseWithId.version;
    }
    uiPopupDispatch({
      promptsList: [...promptsList, newObj],
    });
  }

  const onCopyClick = async (clickedObj) => {
    uiPopupDispatch({
      promptsList: promptsList.map(groupObject => {
        return (clickedObj.id === groupObject.id) ? { ...groupObject, isCopying: true } : groupObject
      })
    });
    const newObj = {
      ...clickedObj,
      "id": 0,
      version: 0,
      isActiveFirst: false,
      isActiveSecond: false,
      isActiveThird: false,
      isDefault: false,
    }
    await createPrompt(newObj);
    const prompts = await getCurrentPrompts();
    const responseWithId = prompts[prompts.length - 1];
    if (responseWithId) {
      newObj.id = responseWithId.id;
      newObj.version = responseWithId.version;
    }
    // now we have new prompt object with id, so we can update the list of prompts
    await createPromptsForNewPartObject(newObj, clickedObj);
    uiPopupDispatch({
      promptsList: [...promptsList.map(groupObject => {
        return (clickedObj.id === groupObject.id) ? { ...groupObject, isCopying: false } : groupObject
      }), newObj],
    });
  }

  return (
    <ul className="ass-list u-w-full u-pr-1">
      {renderPromptsList.map(promptObject => {
        return (
          <li key={promptObject.id} className="ass-list__item">
            <div
              className={"ui-checkbox ui-checkbox--small" + (promptObject[isActiveType] ? " ui-checkbox--checked-svg" : "")}
              onClick={() => setActivePrompt(promptObject)}>
              <div className="ui-checkbox__svg">
                <UiIcon icon="checkmark"/>
              </div>
            </div>
            <div className="ui-checkbox__label flex--center u-pt-0 u-pl-025 u-pr-025 u-h-full flex--1 flex--start bold color-primary"
                 style={{textAlign: "left", overflow: "hidden", display: "flex"}}
                 onClick={() => setActivePrompt(promptObject)}>
              Version {promptObject.version}
            </div>
            {promptObject.isCopying ? <LoadingSpinner size="30"
                                                      style={{width: "32px", height: "32px", backgroundSize: "66% !important"}}/> : (
              <IconButton className="look-icon look-icon--small color-red"
                          onClick={() => onCopyClick(promptObject)}>
                <CloneIcon/>
              </IconButton>
            )}
            <IconButton className="look-icon look-icon--small color-red"
                        onClick={() => onDeleteClick(promptObject)}>
              <DeleteForeverIcon/>
            </IconButton>
          </li>
        )
      })}
      <li className="ass-list__item flex flex--center">
        <span className="link link--blue" onClick={onCreateClick}>
            <IconButton className="look-icon look-icon--small">
            <AddCircleOutlineIcon/>
          </IconButton>
          <span>Add New</span>
        </span>
      </li>
    </ul>
  )
};

export default UiPopupAIContentPrompts

